import React, { useState, useEffect, useMemo } from 'react';
import { SpotVisit, CampingSpot, AppUser, CategorySuggestion, GpxRoute } from '../../types';
import { USER_TIERS } from '../../constants';
import { formatVisitDate, getUserTier, getNextTier, groupVisitsByCounty, enrichVisitsWithSpotData, saveEnrichedVisits } from '../../services/visitService';
import { hasModerationAccess } from '../../services/accessControl';
import { MapPin, Search, X, ChevronRight, ChevronLeft, CheckCircle2, Shield, Loader2, Route } from 'lucide-react';
import TierBadge from '../gamification/TierBadge';
import UserAvatar from '../common/UserAvatar';
import VehicleBadge from '../common/VehicleBadge';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, resolveCategorySuggestion, updateGpxRoute } from '../../services/firebaseService';
import { reverseGeocodeRegion } from '../../services/regionEnrichmentService';
import { useTranslation } from 'react-i18next';
import { StickerBoard } from './StickerBoard';
import PeriodReplayControls, { usePeriodFilter } from './PeriodReplayControls';
import { getSpotAccentColor } from '../../utils/spotVisuals';

interface VisitsTabProps {
  visits: SpotVisit[];
  onSpotClick?: (spotId: string, lat: number, lng: number) => void;
  userPhotoURL?: string | null;
  onCloseOverlay?: () => void;
  spots?: CampingSpot[];
  user?: AppUser | null;
  onVisitsEnriched?: (visits: SpotVisit[]) => void;
}

// Deterministiske aksentfarger er delt med StickerBoard og reise-replayen —
// se utils/spotVisuals.ts (eng-review D5, regresjonstestet).

/**
 * Bilde med fallback. Lagrede photoUrl-er kan være døde (utløpte Mapillary-CDN-lenker)
 * eller i et format nettleseren ikke kan tegne (kamera-RAW lastet opp før valideringen
 * kom på plass). Uten onError ble de stående som brutt bilde-ikon; nå degraderer de
 * til samme fallback som besøk helt uten bilde.
 */
const VisitPhoto: React.FC<{ sources: string[]; className: string; fallback: React.ReactNode }> = ({ sources, className, fallback }) => {
  const [index, setIndex] = useState(0);
  const key = sources.join('|');
  useEffect(() => { setIndex(0); }, [key]);
  const src = sources[index];
  if (!src) return <>{fallback}</>;
  // Flere kandidater per kort: er den første død, prøv neste før vi gir opp.
  return <img src={src} alt="" className={className} loading="lazy" onError={() => setIndex(i => i + 1)} />;
};

const VisitsTab: React.FC<VisitsTabProps> = ({ visits, onSpotClick, userPhotoURL, onCloseOverlay, spots = [], user, onVisitsEnriched }) => {
  const { t } = useTranslation();
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'stickers'>('list');
  const [categorySuggestions, setCategorySuggestions] = useState<CategorySuggestion[]>([]);
  const [pendingRoutes, setPendingRoutes] = useState<GpxRoute[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [routeProcessingId, setRouteProcessingId] = useState<string | null>(null);

  // Datofilter (reise-replay Fase 1, eng-review D1/D6). Default = ingen filter →
  // periodVisits === visits og ALT rendrer som før (regresjonskrav). Filteret
  // styrer liste + klistremerketavle (+ replay-datagrunnlag); tier/progresjon
  // beregnes alltid av ALLE besøk — status påvirkes ikke av et visningsfilter.
  // Delt UI/tilstand med header-inngangen: PeriodReplayControls/usePeriodFilter.
  const { filterFrom, setFilterFrom, filterTo, setFilterTo, hasPeriodFilter, periodVisits } = usePeriodFilter(visits);

  const vc = user?.verifiedSpotCount || 0;
  const currentTier = getUserTier(visits, vc);
  const nextTier = getNextTier(visits, vc);
  const uniqueSpots = new Set(visits.map(v => v.spotId)).size;
  const sortedVisits = useMemo(() => [...periodVisits].sort((a, b) => b.visitedAt - a.visitedAt), [periodVisits]);

  // Berik besøk med county/photo fra spots, deretter reverse geocode for de som fortsatt mangler
  useEffect(() => {
    if (visits.length === 0) return;
    let cancelled = false;

    const run = async () => {
      let list = visits;
      let didChange = false;

      // 1) Berik fra spots-data
      if (spots.length > 0) {
        const { enriched, changed } = enrichVisitsWithSpotData(visits, spots);
        list = enriched;
        didChange = changed;
      }

      // 2) Reverse geocode besøk som fortsatt mangler county
      const missing = list.filter(v => !v.county);
      if (missing.length > 0 && missing.length <= 50) {
        const updates = await Promise.all(
          missing.map(async v => {
            try {
              const region = await reverseGeocodeRegion({ latitude: v.latitude, longitude: v.longitude });
              return region?.county ? { spotId: v.spotId, county: region.county } : null;
            } catch { return null; }
          })
        );
        const countyMap = new Map(updates.filter(Boolean).map(u => [u!.spotId, u!.county]));
        if (countyMap.size > 0) {
          list = list.map(v => !v.county && countyMap.has(v.spotId) ? { ...v, county: countyMap.get(v.spotId)! } : v);
          didChange = true;
        }
      }

      if (cancelled || !didChange || !user?.uid) return;
      onVisitsEnriched?.(list);
      saveEnrichedVisits(user.uid, list).catch(() => {});
    };

    run();
    return () => { cancelled = true; };
  }, [spots.length > 0, visits.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hent ventende kategoriforslag for moderering
  const canModerateCategories = hasModerationAccess(user, 'category_suggestions');
  const canModerateRoutes = hasModerationAccess(user, 'route_suggestions');
  const canModerate = canModerateCategories || canModerateRoutes;

  useEffect(() => {
    if (!canModerateCategories) return;
    const q = query(collection(db, 'category_suggestions'), where('status', '==', 'pending'));
    return onSnapshot(q, (snap) => {
      setCategorySuggestions(snap.docs.map(d => ({ id: d.id, ...d.data() } as CategorySuggestion)));
    }, () => setCategorySuggestions([]));
  }, [canModerateCategories]);

  // Hent ventende ruter for moderering (tier 4+)
  useEffect(() => {
    if (!canModerateRoutes) return;
    const q = query(collection(db, 'routes'), where('status', '==', 'pending'));
    return onSnapshot(q, (snap) => {
      setPendingRoutes(snap.docs.map(d => ({ id: d.id, ...d.data() } as GpxRoute)));
    }, () => setPendingRoutes([]));
  }, [canModerateRoutes]);

  const COMMON_FEATURES = ["FRICAMP", "GAPAHUK", "UTSIKT", "BÅLPLASS", "TOALETT", "BADEPLASS", "VANN", "PICNIC", "KAI/BRYGGE", "SNUPLASS"];
  const handleResolveCategory = async (suggestion: CategorySuggestion, decision: 'approve' | 'reject') => {
    setProcessingId(suggestion.id);
    try {
      await resolveCategorySuggestion(suggestion, decision, COMMON_FEATURES);
    } catch (err) {
      console.error('Failed to resolve suggestion', err);
    }
    setProcessingId(null);
  };

  const handleResolveRoute = async (route: GpxRoute, decision: 'approve' | 'reject') => {
    setRouteProcessingId(route.id);
    try {
      if (decision === 'approve') {
        await updateGpxRoute(route.id, { status: 'public' as any });
      } else {
        await updateGpxRoute(route.id, { status: 'rejected' as any, rejectionReason: 'Avvist av moderator' } as any);
      }
    } catch (err) {
      console.error('Failed to resolve route suggestion', err);
    }
    setRouteProcessingId(null);
  };

  // Gruppér besøk per fylke (av periodfiltrert utvalg)
  const countyGroups = useMemo(() => {
    const groups = groupVisitsByCounty(periodVisits);
    return [...groups.entries()]
      .map(([county, visits]) => ({
        county,
        visits,
        uniqueCount: new Set(visits.map(v => v.spotId)).size,
        // Alle bildekandidater i fylket, nyeste først. Kortet faller videre til
        // neste dersom en lenke er død (VisitPhoto), i stedet for å bli tom.
        photoCandidates: [...new Set(
          [...visits].sort((a, b) => b.visitedAt - a.visitedAt).map(v => v.photoUrl).filter((u): u is string => !!u)
        )].slice(0, 5),
      }))
      .sort((a, b) => b.uniqueCount - a.uniqueCount);
  }, [periodVisits]);

  // Drilldown: besøk i valgt fylke
  const countyVisits = useMemo(() => {
    if (!selectedCounty) return [];
    return sortedVisits.filter(v => (v.county || 'Ukjent fylke') === selectedCounty);
  }, [selectedCounty, sortedVisits]);

  const filteredCountyVisits = useMemo(() => {
    if (!searchQuery.trim()) return countyVisits;
    return countyVisits.filter(v => v.spotName.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [countyVisits, searchQuery]);

  // Finn photo fra spots-arrayet hvis ikke på visit
  const getPhotoForVisit = (visit: SpotVisit): string | undefined => {
    if (visit.photoUrl) return visit.photoUrl;
    const spot = spots.find(s => s.id === visit.spotId);
    // Samme bildeprioritet som minikortene på kartet.
    return spot?.ugcThumbnailUrl || spot?.photoUrls?.[0] || spot?.auto_image_url;
  };

  // ─── DRILLDOWN VIEW ───
  if (selectedCounty) {
    const uniqueInCounty = new Set(countyVisits.map(v => v.spotId)).size;
    return (
      <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
        <button
          onClick={() => { setSelectedCounty(null); setSearchQuery(''); }}
          className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ChevronLeft size={16} />
          <span className="text-tiny font-black uppercase tracking-widest">{t("auto.profile.visitstab.tilbake", "Tilbake")}</span>
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-black text-[var(--text-primary)]">{selectedCounty}</h3>
            <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">{uniqueInCounty} unike plasser</p>
          </div>
        </div>

        {countyVisits.length > 2 && (
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
            <input
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t("auto.profile.visitstab.placeholder.sok_i_besok", "Søk i besøk...")}
              className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl pl-9 pr-9 py-3 text-[12px] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-emerald-500/50 outline-none transition-all"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors p-1 -mr-1">
                <X size={14} />
              </button>
            )}
          </div>
        )}

        <div className="space-y-2">
          {filteredCountyVisits.map((visit, i) => {
            const photo = getPhotoForVisit(visit);
            const accentColor = getSpotAccentColor(visit.spotId);
            return (
              <div
                key={`${visit.spotId}-${visit.visitedAt}-${i}`}
                onClick={() => onSpotClick?.(visit.spotId, visit.latitude, visit.longitude)}
                className={`flex items-center gap-3 p-3 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-primary)] transition-colors ${onSpotClick ? 'cursor-pointer hover:bg-[var(--bg-surface)] active:bg-[var(--bg-surface)]' : ''}`}
              >
                <VisitPhoto
                  sources={photo ? [photo] : []}
                  className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                  fallback={
                    <div className={`w-10 h-10 rounded-xl ${accentColor} flex items-center justify-center flex-shrink-0`}>
                      <MapPin size={14} className="text-white/80" />
                    </div>
                  }
                />
                <div className="flex-1 min-w-0">
                  <span className="text-[12px] font-bold text-[var(--text-primary)] truncate block">{visit.spotName}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-3xs font-black text-[var(--text-muted)]">{formatVisitDate(visit.visitedAt)}</span>
                    {visit.vehicleCategory && <VehicleBadge category={visit.vehicleCategory} compact />}
                  </div>
                </div>
                {onSpotClick && <ChevronRight size={14} className="text-[var(--text-muted)] flex-shrink-0" />}
              </div>
            );
          })}
          {filteredCountyVisits.length === 0 && searchQuery && (
            <div className="bg-[var(--bg-surface-alt)] rounded-2xl p-4 border border-[var(--border-primary)] text-center">
              <p className="text-tiny text-[var(--text-muted)]">Ingen treff for «{searchQuery}»</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── MAIN VIEW ───
  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">

      {/* A) Tier Hero */}
      <div className="flex items-center gap-4">
        <UserAvatar photoURL={userPhotoURL} tier={currentTier.level} size="lg" />
        <div className="flex-1">
          <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-0.5">{t("auto.profile.visitstab.din_status", "Din status")}</p>
          <h2 className="text-xl font-black tracking-tight" style={{ color: currentTier.color }}>
            {t(`tierGuide.tierNames.${currentTier.level}`, currentTier.name)}
          </h2>
          <p className="text-tiny text-[var(--text-secondary)] mt-0.5">{t('profile.unique_spots_visited', '{{count}} unike plasser besøkt', { count: uniqueSpots })}</p>
        </div>
        <TierBadge tier={currentTier.level} showName={false} className="scale-125" />
      </div>

      {/* B) Progresjon til neste tier */}
      {nextTier && (
        <div className="bg-[var(--bg-surface-alt)] rounded-2xl p-4 border border-[var(--border-primary)]">
          <div className="flex justify-between items-center mb-2">
            <span className="text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)]">
              {t('profile.spots_to_next_tier_long', '{{count}} plasser til {{tier}}', { count: Math.max(0, nextTier.threshold - (uniqueSpots + vc)), tier: t(`tierGuide.tierNames.${nextTier.level}`, nextTier.name) })}
            </span>
            <span className="text-2xs font-black text-[var(--text-secondary)]">
              {uniqueSpots + vc} / {nextTier.threshold}
            </span>
          </div>
          <div className="w-full h-1.5 bg-[var(--bg-input)] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-1000 ease-out"
              style={{
                width: `${Math.min(100, ((uniqueSpots + vc) / nextTier.threshold) * 100)}%`,
                backgroundColor: nextTier.color,
                boxShadow: `0 0 8px ${nextTier.color}44`
              }}
            />
          </div>
        </div>
      )}

      {/* C) Fylke-grid vs Klistremerketavle */}
      {visits.length > 0 ? (
        <div className="space-y-4">
          {/* Datofilter + Reisefilm (delt med header-inngangen, eng-review D1) */}
          <PeriodReplayControls
            filterFrom={filterFrom}
            filterTo={filterTo}
            onFilterFromChange={setFilterFrom}
            onFilterToChange={setFilterTo}
            hasPeriodFilter={hasPeriodFilter}
            periodVisits={periodVisits}
            allVisits={visits}
            spots={spots}
            user={user}
          />

          {/* Tab Switcher */}
          <div className="flex bg-[var(--bg-input)] p-1 rounded-2xl border border-[var(--border-primary)] mx-1">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`flex-1 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${viewMode === 'list' ? 'bg-emerald-600 text-white shadow-lg' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
            >
              {t("visitsTab.tabs.list", "Liste over besøk")}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('stickers')}
              className={`flex-1 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${viewMode === 'stickers' ? 'bg-emerald-600 text-white shadow-lg' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
            >
              {t("visitsTab.tabs.stickers", "Klistremerketavle")}
            </button>
          </div>

          {viewMode === 'stickers' && user ? (
            <StickerBoard visits={periodVisits} spots={spots} user={user} />
          ) : (
            <div>
              <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-3">{t("auto.profile.visitstab.besokte_fylker", "Besøkte fylker")}</p>
              {countyGroups.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {countyGroups.map(({ county, uniqueCount, photoCandidates }) => (
                    <button
                      key={county}
                      onClick={() => setSelectedCounty(county)}
                      className="flex items-center gap-3 p-3 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-primary)] hover:bg-[var(--bg-surface)] active:bg-[var(--bg-surface)] transition-colors text-left"
                    >
                      <VisitPhoto
                        sources={photoCandidates}
                        className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                        fallback={
                          <div className="w-9 h-9 rounded-lg bg-emerald-900/50 flex items-center justify-center flex-shrink-0">
                            <MapPin size={14} className="text-emerald-500/60" />
                          </div>
                        }
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-tiny font-bold text-[var(--text-primary)] truncate block">{county}</span>
                        <span className="text-3xs font-black text-[var(--text-muted)]">{uniqueCount} {uniqueCount === 1 ? 'plass' : 'plasser'}</span>
                      </div>
                      <ChevronRight size={12} className="text-[var(--text-muted)] flex-shrink-0" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="bg-[var(--bg-surface-alt)] rounded-2xl p-4 border border-[var(--border-primary)] text-center">
                  <p className="text-tiny text-[var(--text-muted)]">{t("auto.profile.visitstab.besok_plasser_for_a_se_dem_sor", "Besøk plasser for å se dem sortert per fylke")}</p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Tom-tilstand */
        <div className="bg-[var(--bg-surface)] rounded-3xl p-6 border border-[var(--border-secondary)] text-center flex flex-col items-center justify-center space-y-4">
          <UserAvatar photoURL={userPhotoURL} tier={0} size="xl" className="border-2 border-dashed border-[var(--border-secondary)] shadow-lg" />
          <div className="space-y-1">
            <p className="text-[13px] font-black text-[var(--text-primary)]">{t("auto.profile.visitstab.ikke_utforsker_enna", "Ikke Utforsker ennå")}</p>
            <p className="text-tiny text-[var(--text-secondary)]">{t("auto.profile.visitstab.samle_unike_overnattinger_for_", "Samle unike overnattinger for å bygge opp friluftsstatusen din og få farge rundt avataren.")}</p>
          </div>
          <div className="w-full flex justify-between items-center bg-[var(--bg-input)] rounded-xl px-4 py-3 border border-[var(--border-primary)]">
            <span className="text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)] text-center w-full">
              0 / 1 besøk for å bli Utforsker
            </span>
          </div>
          <button
            onClick={() => onCloseOverlay?.()}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-xl text-tiny font-black uppercase tracking-widest transition-colors shadow-[0_0_15px_rgba(16,185,129,0.3)] border border-emerald-500/50"
          >
            {t("auto.profile.visitstab.finn_en_plass_i_kartet", "Finn en plass i kartet")}
          </button>
        </div>
      )}

      {/* E) Modererings-seksjon (tier >= 3) */}
      {canModerate && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Shield size={14} className="text-amber-500" />
            <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)]">{t("auto.profile.visitstab.moderering", "Moderering")}</p>
            {(categorySuggestions.length + pendingRoutes.length) > 0 && (
              <span className="ml-auto bg-amber-500/20 text-amber-400 text-3xs font-black px-2 py-0.5 rounded-lg">
                {categorySuggestions.length + pendingRoutes.length} ventende
              </span>
            )}
          </div>

          {categorySuggestions.length === 0 && pendingRoutes.length === 0 ? (
            <div className="bg-[var(--bg-surface-alt)] rounded-2xl p-4 border border-[var(--border-primary)] text-center">
              <CheckCircle2 size={24} className="text-emerald-500/30 mx-auto mb-2" />
              <p className="text-tiny text-[var(--text-muted)]">{t("auto.profile.visitstab.ingen_ventende_forslag", "Ingen ventende forslag")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {categorySuggestions.map(suggestion => (
                <div key={suggestion.id} className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">
                        Forslag fra {suggestion.userName}
                      </p>
                      <h4 className="text-[var(--text-primary)] font-black text-sm mt-0.5">{suggestion.newName}</h4>
                      {suggestion.oldName !== suggestion.newName && (
                        <p className="text-[var(--text-muted)] text-2xs line-through">{suggestion.oldName}</p>
                      )}
                    </div>
                    <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-4xs font-black uppercase">
                      {suggestion.newFeature}
                    </span>
                  </div>

                  {(suggestion.oldDescription !== suggestion.newDescription) && (
                    <div className="bg-[var(--bg-input)] p-3 rounded-xl border border-[var(--border-primary)] grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-4xs font-black text-[var(--text-muted)] uppercase">{t("auto.profile.visitstab.for", "Før")}</p>
                        <p className="text-[var(--text-secondary)] text-2xs mt-1 line-clamp-2">"{suggestion.oldDescription}"</p>
                      </div>
                      <div>
                        <p className="text-4xs font-black text-emerald-500 uppercase">{t("auto.profile.visitstab.etter", "Etter")}</p>
                        <p className="text-[var(--text-secondary)] text-2xs mt-1 line-clamp-2">"{suggestion.newDescription}"</p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleResolveCategory(suggestion, 'approve')}
                      disabled={processingId === suggestion.id}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 rounded-xl text-3xs font-black uppercase flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-50"
                    >
                      {processingId === suggestion.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Godkjenn
                    </button>
                    <button
                      onClick={() => handleResolveCategory(suggestion, 'reject')}
                      disabled={processingId === suggestion.id}
                      className="flex-1 bg-red-900/20 hover:bg-red-900/40 text-red-400 py-2.5 rounded-xl text-3xs font-black uppercase flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-50"
                    >
                      {t("auto.profile.visitstab.avvis", "Avvis")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Ventende ruteforslag (tier 4+) */}
          {canModerateRoutes && pendingRoutes.length > 0 && (
            <div className="mt-4 space-y-3">
              <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest flex items-center gap-1.5">
                <Route size={10} /> {t("auto.profile.visitstab.ventende_ruter", "Ventende ruter")}
              </p>
              {pendingRoutes.map(route => (
                <div key={route.id} className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">
                        Foreslått av {route.createdByName || 'Anonym'}
                      </p>
                      <h4 className="text-[var(--text-primary)] font-black text-sm mt-0.5">{route.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-4xs font-black px-1.5 py-0.5 rounded border uppercase tracking-widest ${
                          route.difficulty === 'Easy' ? 'text-emerald-400 border-emerald-500/30'
                          : route.difficulty === 'Hard' ? 'text-red-400 border-red-500/30'
                          : route.difficulty === 'Extreme' ? 'text-red-500 border-red-600/30'
                          : 'text-blue-400 border-blue-500/30'
                        }`}>
                          {route.difficulty}
                        </span>
                        {route.region && (
                          <span className="text-3xs font-bold text-emerald-500 uppercase tracking-widest">{route.region}</span>
                        )}
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 text-4xs font-black uppercase">Pending</span>
                  </div>

                  {route.description && (
                    <div className="bg-[var(--bg-input)] p-3 rounded-xl border border-[var(--border-primary)]">
                      <p className="text-[var(--text-secondary)] text-2xs leading-relaxed italic line-clamp-2">"{route.description}"</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleResolveRoute(route, 'approve')}
                      disabled={routeProcessingId === route.id}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 rounded-xl text-3xs font-black uppercase flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-50"
                    >
                      {routeProcessingId === route.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Godkjenn
                    </button>
                    <button
                      onClick={() => handleResolveRoute(route, 'reject')}
                      disabled={routeProcessingId === route.id}
                      className="flex-1 bg-red-900/20 hover:bg-red-900/40 text-red-400 py-2.5 rounded-xl text-3xs font-black uppercase flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-50"
                    >
                      {t("auto.profile.visitstab.avvis", "Avvis")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VisitsTab;
